import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faClock,
  faChartPie,
  faFileInvoiceDollar,
  faAddressCard,
  faDiagramProject,
  faGear,
} from '@fortawesome/pro-regular-svg-icons'

const AI_ASSISTANT_SURVEY_ID = '019db528-bc69-0000-b54d-f2733890257f'

export function openFeedbackSurvey() {
  window.posthog.displaySurvey(AI_ASSISTANT_SURVEY_ID, {
    displayType: 'popover',
    ignoreConditions: true,
    ignoreDelay: true,
  })
}

export type CategoryStyle = {
  icon: IconDefinition
  color: string
  background: string
  border: string
}

export const categoryStyles = {
  'Labor & Time Tracking': { icon: faClock, color: '#52c41a', background: '#f0faf0', border: '#b7e4b7' },
  'Job Costing': { icon: faChartPie, color: '#1677ff', background: '#e6f0ff', border: '#a3c6ff' },
  'Sales & Estimates': { icon: faFileInvoiceDollar, color: '#d48806', background: '#fffbe6', border: '#ffe58f' },
  'Customers': { icon: faAddressCard, color: '#722ed1', background: '#f6efff', border: '#d3adf7' },
  'Jobs & Work Areas': { icon: faDiagramProject, color: '#08979c', background: '#e6fffb', border: '#87e8de' },
  'Setup': { icon: faGear, color: '#595959', background: '#f5f5f5', border: '#d9d9d9' },
} as const satisfies Record<string, CategoryStyle>

export type QuestionCategory = keyof typeof categoryStyles

export type SampleQuestion = {
  question: string
  category: QuestionCategory
}

export const sampleQuestions: SampleQuestion[] = [
  { question: 'How many hours did my crew work last week?', category: 'Labor & Time Tracking' },
  { question: 'What percent of our hours were billable last week?', category: 'Labor & Time Tracking' },
  { question: 'How many unbillable hours do we have so far this week?', category: 'Labor & Time Tracking' },
  { question: 'How many hours did we log yesterday?', category: 'Labor & Time Tracking' },
  { question: 'Who on my crew logged the most hours last week?', category: 'Labor & Time Tracking' },
  { question: "Compare this week's billable hours to last week's.", category: 'Labor & Time Tracking' },
  { question: 'How many total hours have we tracked this month?', category: 'Labor & Time Tracking' },
  { question: "Pull one crew member's hours for the last two weeks.", category: 'Labor & Time Tracking' },
  { question: "What's our billable percentage so far this month?", category: 'Labor & Time Tracking' },
  { question: 'Where did the hours go on my most recent job, by work area?', category: 'Job Costing' },
  { question: 'Which work area on a job used the most hours?', category: 'Job Costing' },
  { question: 'What jobs have we sold this month?', category: 'Sales & Estimates' },
  { question: 'How many estimates did we win vs lose in the last 90 days?', category: 'Sales & Estimates' },
  { question: 'Which of my draft estimates are more than 30 days old?', category: 'Sales & Estimates' },
  { question: "What's my close rate over the last quarter?", category: 'Sales & Estimates' },
  { question: 'Which open estimates should I follow up on first?', category: 'Sales & Estimates' },
  { question: 'List all the jobs for one of my customers.', category: 'Customers' },
  { question: 'Create a new customer for me.', category: 'Customers' },
  { question: 'Add a property to an existing customer.', category: 'Customers' },
  { question: 'Look up a customer and show me their properties and jobs.', category: 'Customers' },
  { question: 'Start a new job for an existing customer.', category: 'Jobs & Work Areas' },
  { question: 'Add a work area to one of my jobs.', category: 'Jobs & Work Areas' },
  { question: 'Create a work area group and set its pricing type.', category: 'Jobs & Work Areas' },
  { question: 'What divisions and cost codes do we have set up?', category: 'Setup' },
]

/**
 * Fisher-Yates shuffle of a copy, then take `count`. Guarantees no duplicates
 * and does not mutate the source. `rng` is injectable for deterministic tests.
 */
export function pickRandomQuestions(
  questions: SampleQuestion[],
  count: number,
  rng: () => number = Math.random
): SampleQuestion[] {
  const shuffled = [...questions]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled.slice(0, count)
}

export type TextPart = {
  type: 'text'
  content: string
}

export type ToolCallPart = {
  type: 'tool_call'
  tool_call_id: string
  tool_name: string
  arguments: Record<string, unknown>
  /** User-facing label from the server ("Getting job details"); absent or null when the tool has none. */
  tool_title?: string | null
}

export type ToolResultPart = {
  type: 'tool_result'
  tool_call_id: string
  content: string
  is_error: boolean
}

export type OptionItem = {
  id: string
  label: string
  description?: string
}

/**
 * The state of a visual element rebuilt from chat history. `pending` means the
 * user never acted on it — except for an option select, which is never
 * resolved because the choice arrives as the user's next message.
 */
export type UiStatus =
  | 'pending'
  | 'submitted'
  | 'answered'
  | 'abandoned'
  | 'approved'
  | 'denied'
  // A consolidated approval where some rows applied and the rest were
  // abandoned after a failure — neither a clean approval nor a clean denial.
  | 'partially_applied'

/**
 * What the user supplied, for the elements that carry it: a form's submitted
 * values, or a question set's answers keyed by page id.
 */
export type UiResolution = Record<string, unknown> | null

/**
 * Present only on parts rebuilt from history. A defined `status` marks the
 * part as historical, which means it must render read-only: the server's
 * pending state does not survive a session restore, so acting on it would
 * only error.
 */
type HistoricalPart = {
  status?: UiStatus
  resolution?: UiResolution
}

export type OptionSelectPart = HistoricalPart & {
  type: 'option_select'
  prompt: string
  options: OptionItem[]
}

export type PermissionRequestPart = HistoricalPart & {
  type: 'permission_request'
  tool_call_id: string
  tool_name: string
  arguments: Record<string, unknown>
  summary?: string
  /** Markdown the write would store, for the tools that store rich text. */
  body?: string
}

/**
 * One pending write inside a consolidated approval card (chat#169).
 *
 * `summary` and `body` are what the single-write card would show for this call,
 * so an expanded row reads exactly as today's card does. `status` is `pending`
 * until the group is decided; after a partial failure the server re-sends the
 * card with each row's outcome filled in. `retryable` goes false once a row has
 * burned its attempts, and the card then offers only Cancel for it.
 */
export type PermissionGroupItem = {
  tool_call_id: string
  summary: string
  /** Markdown the write would store, for the tools that store rich text. */
  body?: string
  arguments: Record<string, unknown>
  status: 'pending' | 'applied' | 'failed' | 'declined'
  error?: string
  retryable: boolean
}

/**
 * A valid, fully-populated row, for tests and mocks to splat and override.
 *
 * Every construction site cares about `status` and sometimes `tool_call_id` or
 * `error`; the rest is filler. Keeping it here means a field added to
 * `PermissionGroupItem` is one edit rather than one per fixture.
 */
export const defaultPermissionGroupItem = (): PermissionGroupItem => ({
  tool_call_id: 'w1',
  summary: 'Change quantity.',
  arguments: {},
  status: 'pending',
  retryable: true,
})

/**
 * N same-type writes sharing one approval (chat#169).
 *
 * Replaces the N `permission_request` cards a fan-out used to produce. A lone
 * write still arrives as a `PermissionRequestPart`, so both renderers are
 * needed: a turn mixing three quantity edits with one work-area create shows
 * one group card and one single card.
 *
 * `count` is `items.length`, sent explicitly because the server merges
 * duplicate targets before emitting — it is the number of operations being
 * approved, not the number the assistant proposed.
 */
export type PermissionGroupRequestPart = HistoricalPart & {
  type: 'permission_group_request'
  group_id: string
  tool_name: string
  count: number
  summary: string
  items: PermissionGroupItem[]
}

export type FormFieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'number'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'lookup'

export type FormOptionItem = {
  value: string
  label: string
}

// Prefill for a lookup field: the chosen record's id plus a label to display.
export type LookupDefault = {
  id: number | string
  label: string
}

export type FormFieldSpec = {
  name: string
  label: string
  type: FormFieldType
  required?: boolean
  options?: FormOptionItem[] | null
  // For a `lookup` field: the entity searched, and (when scoped) the name of
  // the field this one depends on, or a fixed parent id.
  entity?: string | null
  depends_on?: string | null
  parent_id?: number | string | null
  placeholder?: string | null
  help?: string | null
  default?: string | number | boolean | LookupDefault | null
}

export type FormRequestPart = HistoricalPart & {
  type: 'form_request'
  form_id: string
  prompt: string
  submit_label: string
  fields: FormFieldSpec[]
}

export type OptionsPageSpec = {
  page_id: string
  kind: 'options'
  prompt: string
  skippable: boolean
  options: OptionItem[]
}

export type FormPageSpec = {
  page_id: string
  kind: 'form'
  prompt: string
  skippable: boolean
  fields: FormFieldSpec[]
}

export type QuestionPageSpec = OptionsPageSpec | FormPageSpec

export type QuestionSetPart = HistoricalPart & {
  type: 'question_set'
  question_set_id: string
  prompt: string
  pages: QuestionPageSpec[]
}

export type ErrorPart = {
  type: 'error'
  message: string
}

/** A memory chat just saved or updated, shown as a card with Undo (chat#256). */
export type MemorySavedPart = {
  type: 'memory_saved'
  memory_id: string
  content: string
  category: string
}

export type MessagePart =
  | TextPart
  | ToolCallPart
  | ToolResultPart
  | OptionSelectPart
  | PermissionRequestPart
  | PermissionGroupRequestPart
  | FormRequestPart
  | QuestionSetPart
  | MemorySavedPart
  | ErrorPart

export type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  parts: MessagePart[]
}

export type TextChunkEvent = {
  event: 'text_chunk'
  content: string
}

export type ToolCallEvent = {
  event: 'tool_call'
  tool_call_id: string
  tool_name: string
  arguments: Record<string, unknown>
  tool_title?: string | null
}

export type ToolResultEvent = {
  event: 'tool_result'
  tool_call_id: string
  content: string
  is_error: boolean
}

export type OptionSelectEvent = {
  event: 'option_select'
  prompt: string
  options: OptionItem[]
}

export type PermissionRequestEvent = {
  event: 'permission_request'
  tool_call_id: string
  tool_name: string
  arguments: Record<string, unknown>
  summary?: string
  body?: string
}

export type PermissionGroupRequestEvent = {
  event: 'permission_group_request'
  group_id: string
  tool_name: string
  count: number
  summary: string
  items: PermissionGroupItem[]
}

export type FormRequestEvent = {
  event: 'form_request'
  form_id: string
  prompt: string
  submit_label: string
  fields: FormFieldSpec[]
}

export type QuestionSetEvent = {
  event: 'question_set'
  question_set_id: string
  prompt: string
  pages: QuestionPageSpec[]
}

// Sent only because this build declares the memory-card capability; see
// streamSSE in aiAssistantApi.
export type MemorySavedEvent = {
  event: 'memory_saved'
  memory_id: string
  content: string
  category: string
}

export type ChatErrorEvent = {
  event: 'error'
  message: string
}

export type DoneEvent = {
  event: 'done'
}

export type SSEEvent =
  | TextChunkEvent
  | ToolCallEvent
  | ToolResultEvent
  | OptionSelectEvent
  | PermissionRequestEvent
  | PermissionGroupRequestEvent
  | FormRequestEvent
  | QuestionSetEvent
  | MemorySavedEvent
  | ChatErrorEvent
  | DoneEvent

export function shouldInitSession(enabled: boolean, currentSessionId: string | null): boolean {
  return enabled && currentSessionId === null
}

export async function loadSessionData<T>(
  sessionId: string,
  fetchTranscript: (sessionId: string) => Promise<T>,
  restore: (sessionId: string) => Promise<void>
): Promise<T> {
  const [transcript] = await Promise.all([
    fetchTranscript(sessionId),
    restore(sessionId),
  ])
  return transcript
}

export function sseEventToPart(event: Exclude<SSEEvent, DoneEvent>): MessagePart {
  switch (event.event) {
    case 'text_chunk':
      return { type: 'text', content: event.content }
    case 'tool_call':
      return {
        type: 'tool_call',
        tool_call_id: event.tool_call_id,
        tool_name: event.tool_name,
        arguments: event.arguments,
        tool_title: event.tool_title,
      }
    case 'tool_result':
      return {
        type: 'tool_result',
        tool_call_id: event.tool_call_id,
        content: event.content,
        is_error: event.is_error,
      }
    case 'option_select':
      return {
        type: 'option_select',
        prompt: event.prompt,
        options: event.options,
      }
    case 'permission_request':
      return {
        type: 'permission_request',
        tool_call_id: event.tool_call_id,
        tool_name: event.tool_name,
        arguments: event.arguments,
        summary: event.summary,
        body: event.body,
      }
    case 'permission_group_request':
      return {
        type: 'permission_group_request',
        group_id: event.group_id,
        tool_name: event.tool_name,
        count: event.count,
        summary: event.summary,
        items: event.items,
      }
    case 'form_request':
      return {
        type: 'form_request',
        form_id: event.form_id,
        prompt: event.prompt,
        submit_label: event.submit_label,
        fields: event.fields,
      }
    case 'question_set':
      return {
        type: 'question_set',
        question_set_id: event.question_set_id,
        prompt: event.prompt,
        pages: event.pages,
      }
    case 'memory_saved':
      return {
        type: 'memory_saved',
        memory_id: event.memory_id,
        content: event.content,
        category: event.category,
      }
    case 'error':
      return { type: 'error', message: event.message }
    default: {
      const _exhaustive: never = event
      return { type: 'error', message: `Unknown event type: ${(_exhaustive as any).event}` }
    }
  }
}

/**
 * Add a streamed part to a message's parts.
 *
 * Ordinary parts append. A consolidated approval card is keyed by its
 * `group_id` and *replaces* an existing card with the same id, because the
 * server re-sends the card after a partial failure with each row's outcome
 * filled in — appending would leave the stale all-pending copy on screen above
 * the live one. The replacement keeps the card's original position so it stays
 * where the user was reading it.
 */
export function appendPart(parts: MessagePart[], part: MessagePart): MessagePart[] {
  if (part.type === 'memory_saved') {
    // A save then an update of the same memory in one turn: one card, showing
    // the latest content, where the first one was.
    const at = parts.findIndex((p) => p.type === 'memory_saved' && p.memory_id === part.memory_id)
    if (at !== -1) {
      const next = [...parts]
      next[at] = part
      return next
    }
  }
  if (part.type === 'permission_group_request') {
    const at = parts.findIndex(
      (p) => p.type === 'permission_group_request' && p.group_id === part.group_id
    )
    if (at !== -1) {
      const next = [...parts]
      next[at] = part
      return next
    }
  }
  return [...parts, part]
}

type SSELineParserState = {
  eventType: string
  buffer: string
}

export function createSSELineParser(): SSELineParserState {
  return { eventType: '', buffer: '' }
}

export function parseSSEChunk(state: SSELineParserState, chunk: string): SSEEvent[] {
  const events: SSEEvent[] = []
  state.buffer += chunk
  const lines = state.buffer.split('\n')
  state.buffer = lines.pop()!

  for (const line of lines) {
    if (line.startsWith('event:')) {
      state.eventType = line.slice(6).trim()
    } else if (line.startsWith('data:') && state.eventType) {
      const data = JSON.parse(line.slice(5).trim())
      events.push({ ...data, event: state.eventType } as SSEEvent)
      state.eventType = ''
    }
  }

  return events
}
